/**
 * Sequential unlock: unit → module → chapter.
 * - Complete chapter → unlock next chapter (optional chapters don't block).
 * - Complete all required chapters in a module → unlock next module.
 * - Complete all modules in a unit (all required chapters in each) → unlock next unit.
 *
 * @param {Array} units - List of units (with _id, order)
 * @param {Array} chapters - List of chapters with moduleId (and moduleId.unitId) populated
 * @param {Object} progress - { progress: {}, completedChapters: [] }
 * @param {Object} user - { role: 'admin' | 'student' | 'pro' }
 * @returns {{ chapters: Object, modules: Object, units: Object }} Maps of id -> boolean (true = locked)
 */
export function getLockStatusMap(units, chapters, progress, user) {
  const lockStatusMap = {
    chapters: {},
    modules: {},
    units: {}
  };
  if (!progress || !user) return lockStatusMap;

  const toIdStr = (x) => (x?.toString?.() ?? x);

  if (user.role === 'admin') {
    (chapters || []).forEach(chapter => {
      lockStatusMap.chapters[toIdStr(chapter._id)] = false;
    });
    (units || []).forEach(unit => {
      lockStatusMap.units[toIdStr(unit._id)] = false;
    });
    const moduleIds = new Set();
    (chapters || []).forEach(ch => {
      if (ch.moduleId) moduleIds.add(toIdStr(ch.moduleId._id ?? ch.moduleId));
    });
    moduleIds.forEach(moduleId => {
      lockStatusMap.modules[moduleId] = false;
    });
    return lockStatusMap;
  }

  const isChapterCompleted = (chapterId) => {
    const id = typeof chapterId === 'object' && chapterId?._id != null ? chapterId._id : chapterId;
    const idStr = id?.toString?.() ?? id;
    return (progress.completedChapters || []).some(c => {
      const cId = (c && (c._id || c))?.toString?.() ?? c;
      return cId === idStr;
    });
  };

  // Module is "fully completed" when all required chapters are completed (optional don't block)
  const isModuleFullyCompleted = (moduleChapters) =>
    moduleChapters.every(ch => ch.isRequired === false || isChapterCompleted(ch._id));

  const sortedUnits = [...(units || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  let previousUnitFullyCompleted = true;

  for (const unit of sortedUnits) {
    const unitId = toIdStr(unit._id);
    const modulesMap = {};

    (chapters || []).forEach(ch => {
      if (!ch.moduleId || !ch.moduleId._id) return;
      const chUnitId = (ch.moduleId.unitId?._id || ch.moduleId.unitId)?.toString?.();
      if (chUnitId !== unitId) return;
      const moduleId = toIdStr(ch.moduleId._id);
      if (!modulesMap[moduleId]) {
        modulesMap[moduleId] = { module: ch.moduleId, chapters: [] };
      }
      modulesMap[moduleId].chapters.push(ch);
    });

    const sortedModules = Object.values(modulesMap).sort(
      (a, b) => (a.module?.order ?? 0) - (b.module?.order ?? 0)
    );

    // Unit lock: first unit unlocked; others unlock when previous unit is fully completed
    lockStatusMap.units[unitId] = !previousUnitFullyCompleted;

    if (lockStatusMap.units[unitId]) {
      sortedModules.forEach(({ module: mod, chapters: moduleChapters }) => {
        const modId = toIdStr(mod._id ?? mod);
        lockStatusMap.modules[modId] = true;
        moduleChapters.forEach(ch => {
          lockStatusMap.chapters[toIdStr(ch._id)] = true;
        });
      });
      const unitChapters = sortedModules.flatMap(m => m.chapters);
      previousUnitFullyCompleted = unitChapters.every(ch => ch.isRequired === false || isChapterCompleted(ch._id));
      continue;
    }

    let previousModuleFullyCompleted = true;

    for (const { module: mod, chapters: moduleChapters } of sortedModules) {
      moduleChapters.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const modId = toIdStr(mod._id ?? mod);
      const moduleFullyCompleted = isModuleFullyCompleted(moduleChapters);
      const firstChapterUnlocks = previousModuleFullyCompleted && previousUnitFullyCompleted;

      lockStatusMap.modules[modId] = !firstChapterUnlocks;

      if (lockStatusMap.modules[modId]) {
        moduleChapters.forEach(ch => {
          lockStatusMap.chapters[toIdStr(ch._id)] = true;
        });
        previousModuleFullyCompleted = false;
        continue;
      }

      for (let i = 0; i < moduleChapters.length; i++) {
        const currentChapter = moduleChapters[i];
        const cid = toIdStr(currentChapter._id);
        if (i === 0) {
          lockStatusMap.chapters[cid] = !firstChapterUnlocks;
        } else {
          const previousChapter = moduleChapters[i - 1];
          const isPreviousCompleted = isChapterCompleted(previousChapter._id);
          const isPreviousRequired = previousChapter.isRequired !== false;
          lockStatusMap.chapters[cid] = isPreviousRequired && !isPreviousCompleted;
        }
      }

      previousModuleFullyCompleted = moduleFullyCompleted;
    }

    const unitChapters = sortedModules.flatMap(m => m.chapters);
    previousUnitFullyCompleted = unitChapters.every(ch => ch.isRequired === false || isChapterCompleted(ch._id));
  }

  return lockStatusMap;
}

/**
 * Check if a specific chapter is locked for the user.
 * @returns {boolean} true if chapter is locked
 */
export function isChapterLocked(chapterId, units, chapters, progress, user) {
  const map = getLockStatusMap(units, chapters, progress, user);
  const idStr = (chapterId?.toString?.() ?? chapterId);
  return map.chapters[idStr] ?? true; // unknown chapter = locked
}

/**
 * Sort chapters in curriculum order: unit order → module order → chapter order.
 * Use this for "next chapter" navigation so the suggested next module is correct.
 * @param {Array} chapters - Chapters with moduleId (and moduleId.unitId) populated
 * @returns {Array} New array sorted by curriculum order
 */
export function sortChaptersByCurriculumOrder(chapters) {
  if (!chapters || chapters.length === 0) return [];
  return [...chapters].sort((a, b) => {
    const unitOrderA = a.moduleId?.unitId?.order ?? 999;
    const unitOrderB = b.moduleId?.unitId?.order ?? 999;
    if (unitOrderA !== unitOrderB) return unitOrderA - unitOrderB;
    const modOrderA = a.moduleId?.order ?? 999;
    const modOrderB = b.moduleId?.order ?? 999;
    if (modOrderA !== modOrderB) return modOrderA - modOrderB;
    return (a.order ?? 999) - (b.order ?? 999);
  });
}
